import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * DomainRedirect component handles cross-domain redirects
 *
 * Rules:
 * - Marketing domain (getproductworkshop.com) routes:
 *   - Homepage (/)
 *   - Blog (/blog, /blog/*)
 *   - Legal pages (/terms, /privacy)
 *   - SEO files (/sitemap.xml, /rss.xml)
 * - App domain (app.getproductworkshop.com) routes:
 *   - All logged-in experience (/login, /dashboard, /admin, /:slug, etc.)
 * - If user visits app.getproductworkshop.com/:
 *   - Logged in → redirect to /dashboard
 *   - Not logged in → redirect to /login
 * - Blog/SEO routes on app domain → redirect to marketing domain
 * - App routes on marketing domain → redirect to app domain
 */
export default function DomainRedirect() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  useEffect(() => {
    // Wait for auth to load
    if (loading) {
      return;
    }

    const hostname = window.location.hostname;
    const appUrl = import.meta.env.VITE_APP_URL;
    const marketingUrl = import.meta.env.VITE_MARKETING_URL;

    // Skip if we're in development (localhost)
    if (hostname === 'localhost' || hostname.startsWith('127.0.0.1')) {
      return;
    }

    // Skip if environment variables are not set
    if (!appUrl || !marketingUrl) {
      return;
    }

    // Extract hostnames from URLs
    const appHostname = new URL(appUrl).hostname;
    const marketingHostname = new URL(marketingUrl).hostname;

    // Define marketing-only routes (homepage, blog, legal pages)
    const isHomepage = location.pathname === '/';
    const isTermsOrPrivacy = ['/terms', '/privacy'].includes(location.pathname);
    const isBlog = location.pathname === '/blog' || location.pathname.startsWith('/blog/');
    const isSitemapOrRss = ['/sitemap.xml', '/rss.xml'].includes(location.pathname);
    const isMarketingRoute = isHomepage || isTermsOrPrivacy || isBlog || isSitemapOrRss;

    // RULE 1: Homepage (/) on app domain → redirect based on auth state
    if (hostname === appHostname && isHomepage) {
      if (user) {
        navigate('/dashboard', { replace: true });
      } else {
        navigate('/login', { replace: true });
      }
      return;
    }

    // RULE 2: Marketing routes on app domain → redirect to marketing domain
    // (blog, sitemap, rss should only be on marketing domain)
    if (hostname === appHostname && (isBlog || isSitemapOrRss)) {
      const redirectUrl = `${marketingUrl}${location.pathname}${location.search}${location.hash}`;
      window.location.href = redirectUrl;
      return;
    }

    // RULE 3: Homepage (/) on marketing domain → always show marketing (no redirect needed)
    // This is the default behavior, no action needed

    // RULE 4: App routes on marketing domain → redirect to app domain
    // (but keep marketing routes on marketing domain)
    if (hostname === marketingHostname && !isMarketingRoute) {
      const redirectUrl = `${appUrl}${location.pathname}${location.search}${location.hash}`;
      window.location.href = redirectUrl;
      return;
    }

    // RULE 5: Marketing homepage accessed on app domain → redirect to marketing
    // This is handled by RULE 1 above (redirects to login/dashboard instead)

  }, [location, user, loading, navigate]);

  return null;
}
